import { gql } from "@apollo/client";

export const SEND_CASH_RECEIPT_REMINDER = gql`
  mutation SendCashReceiptReminder($amount: Int!, $sellerId: Int!) {
    sendCashReceiptReminder(amount: $amount, sellerId: $sellerId) {
      ok
      error
    }
  }
`;

export const ADMIN_MATCH_SETTLE = gql`
  mutation AdminMatchSettleWithHometaxXlsx(
    $file: Upload!
    $requestDate: String!
    $settleWay: String
    $amountTolerance: Int
  ) {
    adminMatchSettleWithHometaxXlsx(
      file: $file
      requestDate: $requestDate
      settleWay: $settleWay
      amountTolerance: $amountTolerance
    ) {
      summary {
        totalSettles
        totalReceipts
        matched
        missingRequest
        missingReceipt
        mismatch
      }
      matched {
        businessNumber
        sellerId
        merchantNames
        receiptNetAmount
        settleTotalAmount
        settleIds
        storeTitle
      }
      missingRequest {
        businessNumber
        merchantNames
        netAmount
      }
      missingReceipt {
        businessNumber
        sellerId
        settleIds
        totalAmount
        storeTitle
      }
      mismatch {
        businessNumber
        sellerId
        receiptNetAmount
        settleTotalAmount
        diff
        merchantNames
        storeTitle
      }
      total {
        hometaxMerchantNames
        storeTitle
        accountHolder
        bank
        accountNumber
        sellerContactNumber
        businessNumber
        settlePrice
        cashReceiptAmount
        cashReceiptIssueDate
        settlementStatus
        memo
      }
    }
  }
`;

export type AdminMatchSettleVars = {
  file: File;
  requestDate: string; // YYYY-MM-DD
  settleWay?: string | null; // e.g. "팝빌"
  amountTolerance?: number | null; // default 0
};

export type SendCashReceiptReminderVars = {
  amount: number;
  sellerId: number;
};

export type SendCashReceiptReminderData = {
  sendCashReceiptReminder: {
    ok: boolean;
    error?: string | null;
  };
};

export type SettlementMatchSummary = {
  totalSettles: number;
  totalReceipts: number;
  matched: number;
  missingRequest: number;
  missingReceipt: number;
  mismatch: number;
};

export type SettlementMatchGroup = {
  businessNumber: string;
  sellerId?: number | null;
  merchantNames?: string[] | null;
  receiptNetAmount: number;
  settleTotalAmount: number;
  settleIds?: number[] | null;
  storeTitle?: string | null;
};

export type HometaxGroup = {
  businessNumber: string;
  merchantNames?: string[] | null;
  netAmount: number;
};

export type SettleGroup = {
  businessNumber: string;
  sellerId: number;
  settleIds: number[];
  totalAmount: number;
  storeTitle?: string | null;
};

export type SettlementMismatchGroup = {
  businessNumber: string;
  sellerId?: number | null;
  receiptNetAmount: number;
  settleTotalAmount: number;
  diff: number;
  merchantNames?: string[] | null;
  storeTitle?: string | null;
};

export type SettlementTotalItem = {
  hometaxMerchantNames?: string[] | null;
  storeTitle?: string | null;
  accountHolder?: string | null;
  bank?: string | null;
  accountNumber?: string | null;
  sellerContactNumber?: string | null;
  businessNumber?: string | null;
  settlePrice?: number | null;
  cashReceiptAmount?: number | null;
  cashReceiptIssueDate?: string | null;
  settlementStatus?: string | null;
  memo?: string | null;
};

export type AdminMatchSettleData = {
  adminMatchSettleWithHometaxXlsx: {
    summary: SettlementMatchSummary;
    matched: SettlementMatchGroup[];
    missingRequest: HometaxGroup[];
    missingReceipt: SettleGroup[];
    mismatch: SettlementMismatchGroup[];
    total: SettlementTotalItem[];
  };
};

// 포맷터 함수
export const formatKRW = (v: number) => v.toLocaleString("ko-KR");
export const joinNames = (arr?: string[] | null) =>
  arr?.length ? arr.join(", ") : "-";

// 테이블 컬럼 정의
export const matchedColumns = [
  { key: "businessNumber", header: "사업자번호" },
  { key: "sellerId", header: "셀러ID" },
  {
    key: "merchantNames",
    header: "가맹점명(홈택스)",
    render: (r: SettlementMatchGroup) => joinNames(r.merchantNames),
  },
  {
    key: "storeTitle",
    header: "매장명(마감히어로)",
    render: (r: SettlementMatchGroup) => r.storeTitle || "-",
  },
  {
    key: "receiptNetAmount",
    header: "영수증 합계",
    render: (r: SettlementMatchGroup) => formatKRW(r.receiptNetAmount),
  },
  {
    key: "settleTotalAmount",
    header: "정산 합계",
    render: (r: SettlementMatchGroup) => formatKRW(r.settleTotalAmount),
  },
  {
    key: "settleIdsCount",
    header: "정산건수",
    render: (r: SettlementMatchGroup) => r.settleIds?.length ?? 0,
  },
];

export const missingRequestColumns = [
  { key: "businessNumber", header: "사업자번호" },
  {
    key: "merchantNames",
    header: "가맹점명(홈택스)",
    render: (r: HometaxGroup) => joinNames(r.merchantNames),
  },
  {
    key: "netAmount",
    header: "영수증 순합계",
    render: (r: HometaxGroup) => formatKRW(r.netAmount),
  },
];

export const missingReceiptColumns = [
  { key: "businessNumber", header: "사업자번호" },
  { key: "sellerId", header: "셀러ID" },
  {
    key: "storeTitle",
    header: "매장명(마감히어로)",
    render: (r: SettleGroup) => r.storeTitle || "-",
  },
  {
    key: "totalAmount",
    header: "정산 합계",
    render: (r: SettleGroup) => formatKRW(r.totalAmount),
  },
  {
    key: "settleIdsCount",
    header: "정산건수",
    render: (r: SettleGroup) => r.settleIds?.length ?? 0,
  },
];

export const mismatchColumns = [
  { key: "businessNumber", header: "사업자번호" },
  { key: "sellerId", header: "셀러ID" },
  {
    key: "merchantNames",
    header: "가맹점명(홈택스)",
    render: (r: SettlementMismatchGroup) => joinNames(r.merchantNames),
  },
  {
    key: "storeTitle",
    header: "매장명(마감히어로)",
    render: (r: SettlementMismatchGroup) => r.storeTitle || "-",
  },
  {
    key: "receiptNetAmount",
    header: "영수증 합계",
    render: (r: SettlementMismatchGroup) => formatKRW(r.receiptNetAmount),
  },
  {
    key: "settleTotalAmount",
    header: "정산 합계",
    render: (r: SettlementMismatchGroup) => formatKRW(r.settleTotalAmount),
  },
  {
    key: "diff",
    header: "차이(영수증-정산)",
    render: (r: SettlementMismatchGroup) => formatKRW(r.diff),
  },
];

export const totalColumns = [
  {
    key: "hometaxMerchantNames",
    header: "가맹점명(홈택스)",
    render: (r: SettlementTotalItem) => r.hometaxMerchantNames?.[0] || "-",
  },
  {
    key: "storeTitle",
    header: "매장명",
    render: (r: SettlementTotalItem) => r.storeTitle || "-",
  },
  {
    key: "accountHolder",
    header: "예금주명",
    render: (r: SettlementTotalItem) => r.accountHolder || "-",
  },
  {
    key: "bank",
    header: "은행명",
    render: (r: SettlementTotalItem) => r.bank || "-",
  },
  {
    key: "accountNumber",
    header: "계좌번호",
    render: (r: SettlementTotalItem) => r.accountNumber || "-",
  },
  {
    key: "sellerContactNumber",
    header: "사장님 연락처",
    render: (r: SettlementTotalItem) => r.sellerContactNumber || "-",
  },
  {
    key: "businessNumber",
    header: "사업자번호",
    render: (r: SettlementTotalItem) => r.businessNumber || "-",
  },
  {
    key: "settlePrice",
    header: "정산 금액",
    render: (r: SettlementTotalItem) =>
      r.settlePrice != null ? formatKRW(r.settlePrice) : "-",
  },
  {
    key: "cashReceiptAmount",
    header: "현금영수증 발행",
    render: (r: SettlementTotalItem) =>
      r.cashReceiptAmount != null ? formatKRW(r.cashReceiptAmount) : "-",
  },
  {
    key: "cashReceiptIssueDate",
    header: "현영 발행일",
    render: (r: SettlementTotalItem) => r.cashReceiptIssueDate || "-",
  },
  {
    key: "settlementStatus",
    header: "정산현황",
    render: (r: SettlementTotalItem) => r.settlementStatus || "-",
  },
  {
    key: "memo",
    header: "메모",
    render: (r: SettlementTotalItem) => r.memo || "-",
  },
];
